import { ClassNames } from '@emotion/core'
import * as React from 'react'
import ReactModal from 'react-modal'

import { CrossIcon, ScreenClass, ScreenClassRender, Title } from 'main/ui'
import { activateGoogleOptimize } from 'main/util/googleOptimize'
import {
  Container,
  CrossIconContainer,
  Header,
  ModalKind,
  overlayStyles,
  StyledModal,
} from './Modal.styles'

// HACK (alex): cross-bundle compatibility (#viewport only exists in main bundle)
if (document.getElementById('viewport')) {
  ReactModal.setAppElement('#viewport')
}

interface IGoogleOptimize {
  title?: string
}

interface IProps {
  children: React.ReactNode
  googleOptimize?: IGoogleOptimize
  kind: ModalKind
  title: string
  isOpen: boolean
  onRequestClose: () => void
  height?: string
  sideBar?: boolean
  canClose: boolean
}

function Modal(props: IProps) {
  const {
    children,
    googleOptimize,
    kind,
    title,
    isOpen,
    onRequestClose,
    height,
    sideBar,
    canClose,
  } = props
  const titleRef = React.useRef<HTMLHeadingElement>(null)

  React.useEffect(() => {
    activateGoogleOptimize()

    // Ensure that inner HTML of title is updated when Google Optimize attributes have changed. This
    // prevents the following scenario:
    // - Modal title has remained the same, but the Google Optimize attribute was changed/removed
    // - Changing/removing the Google Optimize attribute implies that any AB-testing change should
    //   be reverted, but React would not update the title in the DOM as the title prop had not
    //   changed.
    if (titleRef.current) {
      titleRef.current.innerHTML = title
    }
  }, [JSON.stringify(googleOptimize)])

  return (
    <ClassNames>
      {({ css }) => (
        <StyledModal
          kind={kind}
          overlayClassName={css(overlayStyles)}
          isOpen={isOpen}
          onRequestClose={onRequestClose}
          sideBar={sideBar}
          height={height}
          shouldCloseOnOverlayClick={canClose}
          shouldCloseOnEsc={canClose}
        >
          <Container>
            <Header>
              <ScreenClassRender
                render={(screenClass: ScreenClass) => (
                  <Title
                    kind={['lg', 'xl'].includes(screenClass) ? 'normal' : 'section'}
                    data-go={googleOptimize && googleOptimize.title}
                    ref={titleRef}
                  >
                    {title}
                  </Title>
                )}
              />
              {canClose && (
                <CrossIconContainer onClick={onRequestClose}>
                  <CrossIcon />
                </CrossIconContainer>
              )}
            </Header>

            {children}
          </Container>
        </StyledModal>
      )}
    </ClassNames>
  )
}

Modal.defaultProps = {
  isOpen: true,
  canClose: true,
  kind: 'normal',
}

export default React.memo(Modal)
